"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";
import type { IncomingWhatsApp } from "@/lib/demos/stockBrokingData";

interface IncomingWhatsAppToastProps {
  incoming: IncomingWhatsApp | null;
  onOpenChat: (incoming: IncomingWhatsApp) => void;
  onDismiss: () => void;
}

const KIND_TAG: Record<IncomingWhatsApp["kind"], { label: string; className: string }> = {
  kyc: { label: "KYC", className: "bg-amber-500/15 text-amber-700" },
  activation: { label: "Segment", className: "bg-violet-500/15 text-violet-700" },
  margin: { label: "Margin", className: "bg-rose-500/15 text-rose-700" },
  sip: { label: "SIP", className: "bg-teal-500/15 text-teal-700" },
  general: { label: "Query", className: "bg-blue-500/15 text-blue-700" },
};

/**
 * Reusable real-time "incoming WhatsApp" popup. Slides in from the top-right on
 * any screen, plays a subtle WebAudio chime (unless reduced-motion), and offers
 * a one-tap "Open chat". Purely presentational — the parent owns the trigger
 * timer and the manual "Simulate incoming message" control.
 */
export function IncomingWhatsAppToast({ incoming, onOpenChat, onDismiss }: IncomingWhatsAppToastProps) {
  const reduceMotion = useReducedMotion();

  return (
    <div className="fixed top-20 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm pointer-events-none">
      <AnimatePresence>
        {incoming && (
          <motion.div
            key={incoming.clientCode + incoming.preview}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 60, scale: 0.96 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 60, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="pointer-events-auto rounded-2xl bg-white/95 border border-emerald-500/30 shadow-2xl shadow-emerald-950/40 backdrop-blur-xl overflow-hidden"
            role="alert"
            aria-live="polite"
          >
            {/* Accent progress bar */}
            {!reduceMotion && (
              <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: 12, ease: "linear" }}
                style={{ transformOrigin: "left" }}
                className="h-0.5 bg-gradient-to-r from-emerald-400 to-teal-500"
              />
            )}

            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* WhatsApp avatar */}
                <div className="relative shrink-0">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                    <MessageCircle className="w-5 h-5 text-slate-900" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold text-slate-900 truncate">{incoming.name}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${KIND_TAG[incoming.kind].className}`}>
                        {KIND_TAG[incoming.kind].label}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={onDismiss}
                      className="text-slate-500 hover:text-slate-900 p-0.5 rounded transition-colors shrink-0"
                      aria-label="Dismiss notification"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-[11px] text-emerald-700/90 font-medium mt-0.5">
                    New WhatsApp message · {incoming.phone}
                  </p>
                  <p className="text-xs text-slate-600 mt-1.5 leading-snug line-clamp-2">
                    {incoming.preview}
                  </p>

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => onOpenChat(incoming)}
                      className="flex-1 py-1.5 px-3 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      Open chat
                    </button>
                    <a
                      href={`tel:${incoming.phone.replace(/\s/g, "")}`}
                      className="py-1.5 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                      title="Click to call"
                    >
                      <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Play a short, soft two-note "message received" chime via WebAudio (no asset). */
export function playIncomingChime() {
  if (typeof window === "undefined") return;
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const notes = [
      { f: 660, t: 0 },
      { f: 880, t: 0.12 },
    ];
    notes.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.09, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.22);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.24);
    });
    // Close the context shortly after playback to free resources.
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch {
    /* Audio is a non-essential enhancement — ignore failures. */
  }
}
